import React from "react";

const CoursesSection = () => {
  const courses = [
    {
      name: "Bachelor of Arts",
      shortName: "B.A",
      duration: "3 Years",
      eligibility: "10+2 in any stream",
      seats: "180",
      description:
        "A broad humanities program designed to build strong knowledge in social sciences, language, and general studies.",
      gradient: "from-orange-400 via-amber-500 to-orange-600",
      bgLight: "bg-orange-50",
      subjects: [
        "Hindi",
        "English",
        "Sanskrit",
        "History",
        "Political Science",
        "Sociology",
        "Economics",
        "Education",
        "Home Science",
        "Geography",
      ],
      features: ["Communication Skills", "Personality Development"],
    },
    {
      name: "Bachelor of Science",
      shortName: "B.Sc",
      duration: "3 Years",
      eligibility: "10+2 with Science",
      seats: "90",
      description:
        "A science-focused undergraduate program with practical learning, laboratory exposure, and academic excellence.",
      gradient: "from-sky-500 via-blue-600 to-blue-700",
      bgLight: "bg-blue-50",
      subjects: [
        "Physics",
        "Chemistry",
        "Mathematics",
        "Botany",
        "Zoology",
        "Biology",
        "Computer Science",
      ],
      features: ["Modern Labs", "Practical Training"],
    },
    {
      name: "Bachelor of Commerce",
      shortName: "B.Com",
      duration: "3 Years",
      eligibility: "10+2 with Commerce/Mathematics",
      seats: "120",
      description:
        "A career-oriented commerce program focused on accounting, finance, business management, and economics.",
      gradient: "from-yellow-400 via-yellow-500 to-amber-500",
      bgLight: "bg-yellow-50",
      subjects: [
        "Accounting",
        "Business Studies",
        "Finance",
        "Economics",
        "Business Law",
        "Taxation",
        "Management",
        "Marketing",
      ],
      features: ["Career Guidance", "Business Learning"],
    },
    {
      name: "Master of Arts",
      shortName: "M.A",
      duration: "2 Years",
      eligibility: "Graduation in relevant subject",
      seats: "60",
      description:
        "A postgraduate humanities program for advanced academic knowledge and subject specialization.",
      gradient: "from-purple-500 via-violet-600 to-indigo-700",
      bgLight: "bg-purple-50",
      subjects: [
        "Hindi",
        "English",
        "Sanskrit",
        "History",
        "Political Science",
        "Sociology",
        "Economics",
        "Education",
        "Home Science",
        "Geography",
      ],
      features: ["Advanced Study", "Research Orientation"],
    },
    {
      name: "Master of Science",
      shortName: "M.Sc",
      duration: "2 Years",
      eligibility: "B.Sc in relevant subject",
      seats: "40",
      description:
        "A postgraduate science program offering advanced theoretical and practical subject knowledge.",
      gradient: "from-emerald-500 via-teal-600 to-cyan-700",
      bgLight: "bg-emerald-50",
      subjects: [
        "Physics",
        "Chemistry",
        "Mathematics",
        "Botany",
        "Zoology",
        "Biology",
      ],
      features: ["Research Projects", "Lab-Based Learning"],
    },
    {
      name: "Master of Commerce",
      shortName: "M.Com",
      duration: "2 Years",
      eligibility: "B.Com or equivalent",
      seats: "40",
      description:
        "A postgraduate commerce program focused on advanced accounting, finance, business, and management studies.",
      gradient: "from-rose-500 via-red-500 to-orange-600",
      bgLight: "bg-rose-50",
      subjects: [
        "Advanced Accounting",
        "Corporate Finance",
        "Business Economics",
        "Taxation",
        "Banking",
        "Marketing Management",
        "Business Law",
        "Research Methodology",
      ],
      features: ["Finance Skills", "Professional Growth"],
    },
  ];

  return (
    <section className="w-full bg-gradient-to-br from-amber-50 via-white to-blue-50 py-16 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-yellow-400 rounded-full"></div>
            <div className="w-8 h-1 bg-amber-500 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-600 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold">
              <span className="bg-gradient-to-r from-yellow-500 via-amber-600 to-blue-700 bg-clip-text text-transparent">
                Our Academic Programs
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-blue-700 rounded-full mx-auto mt-3"></div>
            <p className="text-gray-600 text-sm mt-4">
              Choose Your Path to Excellence
            </p>
          </div>

          {/* Courses Horizontal Scroll Row */}
          <div className="overflow-x-auto pb-5 custom-scroll-x">
            <div className="flex gap-6 min-w-max">
              {courses.map((course, idx) => (
                <div
                  key={idx}
                  className="group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 w-[300px] md:w-[340px] shrink-0"
                >
                  {/* Card Header */}
                  <div className={`bg-gradient-to-r ${course.gradient} p-4`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-white font-bold text-2xl">
                          {course.shortName}
                        </h3>
                        <p className="text-white/90 text-xs mt-0.5">
                          {course.name}
                        </p>
                      </div>

                      <div className="bg-white/20 rounded-full px-2 py-1 shrink-0">
                        <span className="text-white text-xs font-semibold">
                          {course.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <p className="text-gray-600 text-xs leading-relaxed mb-3 min-h-[64px]">
                      {course.description}
                    </p>

                    <div className="space-y-1 mb-3">
                      <div className="flex items-start gap-1.5">
                        <span className="text-yellow-500 text-xs">•</span>
                        <span className="text-xs text-gray-700">
                          <span className="font-semibold">Eligibility:</span>{" "}
                          {course.eligibility}
                        </span>
                      </div>

                      <div className="flex items-start gap-1.5">
                        <span className="text-amber-500 text-xs">•</span>
                        <span className="text-xs text-gray-700">
                          <span className="font-semibold">Seats:</span>{" "}
                          {course.seats}
                        </span>
                      </div>
                    </div>

                    {/* Subjects One Row Scroll */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-800 mb-1.5">
                        Available Subjects:
                      </p>

                      <div className="overflow-x-auto pb-2 custom-scroll-x">
                        <div className="flex gap-1.5 min-w-max">
                          {course.subjects.map((subject, i) => (
                            <span
                              key={i}
                              className={`px-2 py-0.5 rounded-full text-xs ${course.bgLight} text-gray-700 border border-gray-100 whitespace-nowrap`}
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className={`${course.bgLight} rounded-lg p-2 mb-3`}>
                      <div className="flex flex-wrap gap-2">
                        {course.features.map((feature, i) => (
                          <span key={i} className="text-xs text-gray-700">
                            ✓ {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    <button
                      className={`w-full py-2 rounded-lg font-semibold text-sm transition-all duration-300 bg-gradient-to-r ${course.gradient} text-white hover:shadow-md`}
                    >
                      Apply Now →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Admission Banner */}
          <div className="mt-10 bg-gradient-to-r from-yellow-400 via-amber-500 to-blue-600 rounded-xl p-5">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <div className="text-center md:text-left">
                <p className="text-white font-bold text-sm">
                  Admissions Open for 2024-25
                </p>
                <p className="text-white/90 text-xs mt-1">
                  Limited Seats Available • Apply Today
                </p>
              </div>

              <div className="flex gap-2">
                <button className="px-4 py-1.5 bg-white text-blue-900 rounded-lg font-semibold text-xs hover:bg-gray-100 transition">
                  Download Prospectus
                </button>
                <button className="px-4 py-1.5 bg-transparent border border-white text-white rounded-lg font-semibold text-xs hover:bg-white hover:text-blue-900 transition">
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-8 flex justify-center gap-1.5">
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scroll-x::-webkit-scrollbar {
          height: 5px;
        }

        .custom-scroll-x::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 20px;
        }

        .custom-scroll-x::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 20px;
        }
      `}</style>
    </section>
  );
};

export default CoursesSection;